import React, { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import CircularLoader from "../../components/common/CircularLoader/CircularLoader";
import NoDataFound from "../../components/common/NoDataFound/NoDataFound";
import { useAppDispatch, useAppSelector } from "../../redux/reduxHooks";
import type {
  AccountRow,
  ChannelCatalogItem,
  ConnectionHubCloseReason,
  ConnectionHubCloseEvent,
  ConnectionHubContextValue,
  ConnectionHubOpenOptions,
  InboxGroup,
  QuickConnectLaunch,
  SetupResponse,
  StandaloneCard,
} from "./api/connectionHub.types";
import { CONNECTION_HUB_CLOSE_REASON } from "./api/connectionHub.types";
import { REQUEST_STATUS } from "./constants/connectionHub.constants";
import { isFailedStatus, isVerifying } from "./constants/connectionStatus.constants";
import { closeConnectionHubSession, openConnectionHubSession, removeConnectionAccountFromCache, replaceSelectedIpIds } from "./state/connectionHubSlice";
import {
  selectConnectionCatalog,
  selectConnectionEmailSuggestions,
  selectConnectionHubSession,
  selectConnectionSocketState,
} from "./state/connectionHub.selectors";
import { fetchConnectionAccounts, fetchConnectionChannels, fetchConnectionEmailSuggestions } from "./state/connectionHub.thunks";
import { useConnectFlow, CONNECT_FLOW_STATUS, hasPendingSubBrands } from "./hooks/useConnectFlow";
import { isCredentialFailure, takesPassword } from "./hooks/useRecheck";
import { isForwardingOnlyProvider } from "./constants/providerSetupGuides.constants";
import ConnectModalShell from "./components/modal/ConnectModalShell/ConnectModalShell";
import { connectionHubLogger } from "./utils/logger";
import { connectionHubApi } from "./api/connectionHub.api";
import QuickConnectModal, { quickAccountName, type QuickAccount } from "./components/accounts/QuickConnectModal/QuickConnectModal";
import { startQuickConnect } from "./adapters/quickConnect.adapter";
import { IpRestS } from "../../rest/ipRest-service";
import { getLocalUser } from "../../components/functions/utilities";
import { fetchAccessTokens } from "../../redux/accessTokens/accessTokens";
import CredentialsStep from "./components/modal/CredentialsStep/CredentialsStep";
import type { EditingChannel } from "./components/modal/CredentialsStep/CredentialsStep.types";
import FinishSetupStep from "./components/modal/FinishSetupStep/FinishSetupStep";
import SubBrandStep from "./components/modal/SubBrandStep/SubBrandStep";
import DelegatedViewStep from "./components/modal/DelegatedViewStep/DelegatedViewStep";
import AccountListModal from "./components/accounts/AccountListModal/AccountListModal";
import RemoveAccountDialog from "./components/accounts/RemoveAccountDialog/RemoveAccountDialog";

export const ConnectionHubContext = createContext<ConnectionHubContextValue | null>(null);

interface ConnectionHubProviderProps { children: ReactNode; }
interface ActiveLaunchCallbacks extends Pick<ConnectionHubOpenOptions, "onSuccess" | "onClose"> { agencyClientId: number | null; }

const setupFromResult = (result: { inboxes: InboxGroup[]; standalone: StandaloneCard[] }): SetupResponse => ({
  inboxes: result.inboxes,
  standalone: result.standalone,
});

const setupWithCard = (setup: SetupResponse, card: InboxGroup | StandaloneCard): SetupResponse => {
  if ("inbox" in card) {
    const index = setup.inboxes.findIndex((group) => group.inbox.id === card.inbox.id);
    const inboxes = [...setup.inboxes];
    if (index >= 0) inboxes[index] = card;
    else inboxes.push(card);
    return { ...setup, inboxes };
  }
  const index = setup.standalone.findIndex((item) => item.connection.connectionId === card.connection.connectionId);
  const standalone = [...setup.standalone];
  if (index >= 0) standalone[index] = card;
  else standalone.push(card);
  return { ...setup, standalone };
};

export const ConnectionHubProvider = ({ children }: ConnectionHubProviderProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectConnectionHubSession);
  const catalog = useAppSelector(selectConnectionCatalog);
  const emailSuggestions = useAppSelector(selectConnectionEmailSuggestions);
  const socket = useAppSelector(selectConnectionSocketState);
  const {
    state: flowState,
    resolve: resolveFlow,
    retry: retryFlow,
    completeCredentials,
    applyCardUpdate,
    applySocketUpdate,
    goToStep,
    goToEdit,
    replaceIpIds: replaceFlowIpIds,
    reset: resetFlow,
  } = useConnectFlow();
  const [removalTarget, setRemovalTarget] = useState<AccountRow | null>(null);
  const launchIdRef = useRef(0);
  const callbacksRef = useRef<ActiveLaunchCallbacks | null>(null);
  const credentialsFlushRef = useRef<(() => Promise<boolean>) | null>(null);

  const notifyClosed = useCallback((event: ConnectionHubCloseEvent): void => {
    const callbacks = callbacksRef.current;
    callbacksRef.current = null;
    try {
      callbacks?.onClose?.(event);
    } catch (error) {
      connectionHubLogger.error(error, { errorCode: "CONNECTION_CLOSE_CALLBACK_FAILED", reason: event.reason });
    }
  }, []);

  const dismiss = useCallback((reason: ConnectionHubCloseReason, ipId?: string): void => {
    launchIdRef.current += 1;
    credentialsFlushRef.current = null;
    resetFlow();
    dispatch(closeConnectionHubSession());
    void dispatch(fetchConnectionChannels({ agencyClientId: session.agencyClientId }));
    notifyClosed({ reason, ...(ipId ? { ipId } : {}) });
  }, [dispatch, notifyClosed, resetFlow, session.agencyClientId]);

  const open = useCallback((options: ConnectionHubOpenOptions): void => {
    const ipIds = Array.from(new Set((options?.ipIds ?? []).filter((ipId) => typeof ipId === "string" && ipId.length > 0)));
    const valid = options && ((options.mode === "single" && ipIds.length === 1) || (options.mode === "multi" && ipIds.length > 0));
    if (!valid) {
      connectionHubLogger.error(new Error("Invalid ConnectionHub launch options"), { errorCode: "CONNECTION_INVALID_LAUNCH", mode: options?.mode, ipIdCount: ipIds.length });
      return;
    }
    if (callbacksRef.current) notifyClosed({ reason: CONNECTION_HUB_CLOSE_REASON.REPLACED });
    const agencyClientId = options.agencyClientId ?? null;
    callbacksRef.current = { agencyClientId, onSuccess: options.onSuccess, onClose: options.onClose };
    const companyChanged = session.agencyClientId !== agencyClientId;
    const shouldFetch = companyChanged || catalog.status === REQUEST_STATUS.IDLE || catalog.status === REQUEST_STATUS.FAILED;
    const launchId = ++launchIdRef.current;
    dispatch(openConnectionHubSession({ ...options, ipIds }));
    void resolveFlow({ launchId, ipIds, mode: options.mode, agencyClientId, connectionId: options.connectionId, startNew: options.startNew });
    if (shouldFetch) void dispatch(fetchConnectionChannels({ agencyClientId }));
  }, [catalog.status, dispatch, notifyClosed, resolveFlow, session.agencyClientId]);

  // Re-resolves in place rather than going through `open`, so the launch's success/close callbacks survive.
  const openStartNew = useCallback((ipId: string): void => {
    const launchId = ++launchIdRef.current;
    void resolveFlow({ launchId, ipIds: [ipId], mode: "single", agencyClientId: session.agencyClientId, startNew: true });
  }, [resolveFlow, session.agencyClientId]);

  // Same resolve the grid used to do for an account row, so the server picks the step that connection needs.
  const openConnection = useCallback((ipId: string, connectionId: string): void => {
    const launchId = ++launchIdRef.current;
    void resolveFlow({ launchId, ipIds: [ipId], mode: "single", agencyClientId: session.agencyClientId, connectionId });
  }, [resolveFlow, session.agencyClientId]);

  const close = useCallback((): void => {
    void (async () => {
      const canClose = credentialsFlushRef.current ? await credentialsFlushRef.current() : true;
      if (canClose) dismiss(CONNECTION_HUB_CLOSE_REASON.PROGRAMMATIC);
    })();
  }, [dismiss]);

  const complete = useCallback((setup: SetupResponse): void => {
    const callbacks = callbacksRef.current;
    if (!callbacks) return;
    try {
      callbacks.onSuccess?.({ mode: flowState.mode, ipIds: flowState.ipIds, setup });
    } catch (error) {
      connectionHubLogger.error(error, { errorCode: "CONNECTION_SUCCESS_CALLBACK_FAILED" });
    }
    dismiss(CONNECTION_HUB_CLOSE_REASON.COMPLETED);
  }, [dismiss, flowState.ipIds, flowState.mode]);

  const finishSubBrands = useCallback((card: InboxGroup | StandaloneCard): void => {
    if (!flowState.result) return;
    const next = setupWithCard(setupFromResult(flowState.result), card);
    if (hasPendingSubBrands(next)) applyCardUpdate(card);
    else complete(next);
  }, [applyCardUpdate, complete, flowState.result]);

  useEffect(() => {
    const activeLaunch = callbacksRef.current;
    if (activeLaunch && activeLaunch.agencyClientId !== session.agencyClientId) dismiss(CONNECTION_HUB_CLOSE_REASON.REPLACED);
  }, [dismiss, session.agencyClientId]);

  useEffect(() => {
    if (flowState.step !== "accounts") setRemovalTarget(null);
  }, [flowState.step]);

  useEffect(() => {
    if (session.isOpen && socket.latest) applySocketUpdate(socket.latest);
  }, [applySocketUpdate, session.isOpen, socket.revision]);

  useEffect(() => {
    if (!session.isOpen || flowState.status !== CONNECT_FLOW_STATUS.RESOLVED || flowState.step !== "credentials" || emailSuggestions.status !== REQUEST_STATUS.IDLE) return;
    void dispatch(fetchConnectionEmailSuggestions({ agencyClientId: session.agencyClientId }));
  }, [dispatch, emailSuggestions.status, flowState.status, flowState.step, session.agencyClientId, session.isOpen]);

  // Quick connect is not a hub flow — there is no resolve, no steps. The provider only owns the
  // account list so the redirect is a deliberate second click rather than the first one.
  const [quickTarget, setQuickTarget] = useState<QuickConnectLaunch | null>(null);
  const [quickRemoval, setQuickRemoval] = useState<QuickAccount | null>(null);

  const openQuick = useCallback((target: QuickConnectLaunch): void => {
    setQuickRemoval(null);
    setQuickTarget(target);
  }, []);

  const closeQuick = useCallback((): void => {
    setQuickRemoval(null);
    setQuickTarget(null);
  }, []);

  const startQuickRedirect = useCallback((): void => {
    if (!quickTarget) return;
    const currentUser = getLocalUser();
    const user = {
      ...currentUser,
      companyId: session.agencyClientId ?? currentUser?.companyId,
    };
    // step 2 clears Integration.ts's wizard gate so the OAuth redirect happens on this click.
    startQuickConnect({
      ip: quickTarget as Parameters<typeof startQuickConnect>[0]["ip"],
      user,
      step: 2,
      setStep: () => undefined,
      setIp: () => undefined,
    });
  }, [quickTarget, session.agencyClientId]);

  const refreshConnectionChannels = useCallback(async (): Promise<void> => {
    if (session.isOpen) await dispatch(fetchConnectionChannels({ agencyClientId: session.agencyClientId })).unwrap();
  }, [dispatch, session.agencyClientId, session.isOpen]);

  const value = useMemo<ConnectionHubContextValue>(() => ({ open, openQuick, close, refreshConnectionChannels, isOpen: session.isOpen }), [close, open, openQuick, refreshConnectionChannels, session.isOpen]);
  const channels = useMemo(() => catalog.data?.sections.flatMap((section) => section.channels) ?? [], [catalog.data]);
  const resolved = session.isOpen && flowState.status === CONNECT_FLOW_STATUS.RESOLVED && Boolean(flowState.result);
  const needsCatalog = resolved && flowState.step !== "delegatedView" && !catalog.data;
  const showResolvingShell = session.isOpen && flowState.status === CONNECT_FLOW_STATUS.RESOLVING;
  const showErrorShell = session.isOpen && flowState.status === CONNECT_FLOW_STATUS.FAILED;
  const showCatalogError = needsCatalog && catalog.status === REQUEST_STATUS.FAILED;
  // A button press names its own target; the resolve-time editContext is only the fallback for a server-started edit.
  const editContext = useMemo(() => {
    const result = flowState.result;
    const target = flowState.activeEdit ?? (result?.editContext
      ? { connectionId: result.editContext.connectionId, type: result.editContext.type, inboxId: result.editContext.type === "inboxRecheck" ? result.editContext.inboxId : null }
      : null);
    if (!target || !result) return null;
    if (target.type === "inboxRecheck") {
      const group = target.inboxId
        ? result.inboxes.find((item) => item.inbox.id === target.inboxId)
        : result.inboxes.find((item) => item.covers.some((cover) => cover.connectionId === target.connectionId));
      return group ? { type: "inboxRecheck" as const, group } : null;
    }
    const owner = result.inboxes.find((group) => group.covers.some((item) => item.connectionId === target.connectionId));
    const cover = owner?.covers.find((item) => item.connectionId === target.connectionId);
    const standalone = result.standalone.find((item) => item.connection.connectionId === target.connectionId);
    const connection = standalone?.connection ?? (cover ? {
      connectionId: cover.connectionId,
      ipId: cover.ipId,
      ipName: cover.ipName,
      status: cover.status,
      failureReason: cover.failureReason,
      loginEmail: owner?.inbox.email ?? null,
      otpMode: cover.otpMode,
      inboxId: owner?.inbox.id ?? null,
      hasPassword: cover.hasPassword,
      pendingSubBrands: cover.pendingSubBrands,
      selectedSubBrands: cover.selectedSubBrands,
      connectedAt: null,
    } : null);
    const channel = connection ? channels.find((item) => item.ipId === connection.ipId) : null;
    return connection && channel ? { type: "credentials" as const, connection, channel } : null;
  }, [channels, flowState.activeEdit, flowState.result]);
  // Credentials edits one channel; an inbox recheck resubmits every channel sharing that mailbox.
  const editing = useMemo<EditingChannel[]>(() => {
    if (!editContext) return [];
    if (editContext.type === "credentials") {
      const { connection } = editContext;
      const owner = flowState.result?.inboxes.find((item) => item.inbox.id === connection.inboxId);
      return connection.loginEmail
        ? [{
            ipId: connection.ipId,
            email: connection.loginEmail,
            hasPassword: connection.hasPassword,
            otpMode: connection.otpMode,
            inboxOwner: owner?.inbox.ownerType ?? null,
          }]
        : [];
    }
    const { group } = editContext;
    return group.covers.map((cover) => ({
      ipId: cover.ipId,
      email: group.inbox.email,
      hasPassword: cover.hasPassword,
      otpMode: cover.otpMode,
      inboxOwner: group.inbox.ownerType,
    }));
  }, [editContext, flowState.result]);
  const editContextMissing = resolved && flowState.step === "edit" && !editContext;
  const setup = useMemo(() => (flowState.result ? setupFromResult(flowState.result) : null), [flowState.result]);
  const accountsChannel = useMemo(
    () => (flowState.activeIpId ? channels.find((item) => item.ipId === flowState.activeIpId) ?? null : null),
    [channels, flowState.activeIpId]
  );
  // Finish Setup's Back needs one channel to show accounts for; with several in play there is no right answer.
  const accountsIpId = useMemo(() => {
    if (!setup) return null;
    const ipIds = new Set([
      ...setup.inboxes.flatMap((group) => group.covers.map((cover) => cover.ipId)),
      ...setup.standalone.map((card) => card.connection.ipId),
    ]);
    return ipIds.size === 1 ? Array.from(ipIds)[0] : null;
  }, [setup]);
  const editIpId = editContext
    ? (editContext.type === "credentials" ? editContext.connection.ipId : editContext.group.covers[0]?.ipId ?? null)
    : null;

  // A failed verification is fixed by changing the password, so Finish Setup hands the user straight to the
  // credentials step instead of offering a button. Once per failure: leaving the edit without saving must not
  // bounce them back, but the same channel failing again after a new password does open it again.
  const autoEditedRef = useRef<Set<string>>(new Set());
  useEffect(() => { autoEditedRef.current = new Set(); }, [flowState.launchId]);
  useEffect(() => {
    if (!session.isOpen || flowState.step !== "finishSetup" || !setup) return;
    // Every failed channel that takes a password goes to the credentials step first; OTP-only ones
    // skip it. Saving keeps the status, so the inbox card then runs the recheck from it.
    const connections = [
      ...setup.inboxes
        .filter((group) => group.inbox.ownerType !== "delegate")
        .flatMap((group) => group.covers
          .filter((cover) => takesPassword(cover) &&
            !(isForwardingOnlyProvider(group.inbox.provider) && isCredentialFailure(cover)))
          .map((cover) => ({ connectionId: cover.connectionId, status: cover.status }))),
      ...setup.standalone.map((card) => ({ connectionId: card.connection.connectionId, status: card.status })),
    ];
    connections.forEach(({ connectionId, status }) => {
      if (!isFailedStatus(status)) autoEditedRef.current.delete(connectionId);
    });
    const failed = connections.find(({ connectionId, status }) =>
      isFailedStatus(status) && !autoEditedRef.current.has(connectionId));
    if (!failed) return;
    autoEditedRef.current.add(failed.connectionId);
    goToEdit({ connectionId: failed.connectionId, type: "credentials" });
  }, [flowState.step, goToEdit, session.isOpen, setup]);

  // The accounts step needs a channel from the catalog; without one there is nothing to show.
  useEffect(() => {
    if (flowState.step === "accounts" && catalog.data && !accountsChannel) goToStep("finishSetup");
  }, [accountsChannel, catalog.data, flowState.step, goToStep]);

  useEffect(() => {
    if (editContextMissing && catalog.data) goToStep("finishSetup");
  }, [catalog.data, editContextMissing, goToStep]);
  const verificationInFlight = useMemo(() => {
    if (!setup) return false;
    return setup.inboxes.some((group) => isVerifying(group.status) || group.covers.some((cover) => isVerifying(cover.status))) ||
      setup.standalone.some((card) => isVerifying(card.status));
  }, [setup]);

  return (
    <ConnectionHubContext.Provider value={value}>
      {children}
      <ConnectModalShell
        visible={showResolvingShell || showErrorShell || needsCatalog}
        primaryLabel={showErrorShell || showCatalogError ? "Retry" : "Continue"}
        primaryDisabled={showResolvingShell || (needsCatalog && !showCatalogError)}
        onPrimary={() => {
          if (showErrorShell) retryFlow();
          else if (showCatalogError) void dispatch(fetchConnectionChannels({ agencyClientId: session.agencyClientId }));
        }}
        onClose={() => dismiss(CONNECTION_HUB_CLOSE_REASON.CLOSE_BUTTON)}
        onDismiss={(reason) => dismiss(reason === "escape" ? CONNECTION_HUB_CLOSE_REASON.ESCAPE : CONNECTION_HUB_CLOSE_REASON.BACKDROP)}
      >
        {showErrorShell || showCatalogError ? (
          <NoDataFound title={showErrorShell ? "Unable to start connection setup" : "Unable to load marketplace details"} subTitle={showErrorShell ? flowState.error?.message : catalog.error?.message} />
        ) : <CircularLoader />}
      </ConnectModalShell>

      {resolved && flowState.result && catalog.data && flowState.step === "credentials" ? (
        <CredentialsStep
          key={flowState.launchId}
          launchId={flowState.launchId}
          visible
          requestedIpIds={flowState.result.channelsNeedingCredentials}
          channels={channels}
          drafts={flowState.result.drafts}
          emailSuggestions={emailSuggestions.data}
          agencyClientId={session.agencyClientId}
          onClose={dismiss}
          onSetupComplete={completeCredentials}
          onSelectionChange={(ipIds) => { replaceFlowIpIds(ipIds); dispatch(replaceSelectedIpIds(ipIds)); }}
          registerFlush={(flush) => { credentialsFlushRef.current = flush; }}
        />
      ) : null}

      {resolved && setup && catalog.data && flowState.step === "finishSetup" ? (
        <FinishSetupStep
          visible
          setup={setup}
          channels={channels}
          agencyClientId={session.agencyClientId}
          onCardUpdated={applyCardUpdate}
          {...(verificationInFlight || !accountsIpId ? {} : { onBack: () => goToStep("accounts", accountsIpId) })}
          onDone={() => complete(setup)}
          onClose={dismiss}
        />
      ) : null}

      {/* Editing reuses the credentials step with the emails locked, then rejoins the normal flow. */}
      {resolved && flowState.result && catalog.data && flowState.step === "edit" && editing.length ? (
        <CredentialsStep
          key={`edit-${editing.map((item) => item.ipId).join(",")}`}
          launchId={flowState.launchId}
          visible
          requestedIpIds={editing.map((item) => item.ipId)}
          channels={channels}
          drafts={[]}
          editing={editing}
          emailSuggestions={emailSuggestions.data}
          agencyClientId={session.agencyClientId}
          {...(verificationInFlight || !(editIpId ?? accountsIpId) ? {} : { onBack: () => goToStep("accounts", editIpId ?? accountsIpId) })}
          onClose={dismiss}
          onSetupComplete={completeCredentials}
          onSelectionChange={() => undefined}
        />
      ) : null}

      {resolved && catalog.data && flowState.step === "accounts" && accountsChannel ? (
        <>
          <AccountListModal
            visible={!removalTarget}
            channel={accountsChannel}
            agencyClientId={session.agencyClientId}
            onConnectNew={() => { openStartNew(accountsChannel.ipId); }}
            onOpenAccount={(row) => { openConnection(accountsChannel.ipId, row.connection.connectionId); }}
            onRemoveAccount={setRemovalTarget}
            onClose={() => dismiss(CONNECTION_HUB_CLOSE_REASON.CLOSE_BUTTON)}
          />
          {removalTarget ? (
            <RemoveAccountDialog
              visible
              channel={accountsChannel}
              accountName={removalTarget.connection.loginEmail ?? removalTarget.connection.ipName}
              onDisconnect={async () => { await connectionHubApi.removeConnection(removalTarget.connection.connectionId, session.agencyClientId); }}
              onRemoved={() => {
                dispatch(removeConnectionAccountFromCache({ ipId: accountsChannel.ipId, connectionId: removalTarget.connection.connectionId }));
                // Clearing the target makes the list visible again, and its own effect refetches the rows.
                setRemovalTarget(null);
                void dispatch(fetchConnectionChannels({ agencyClientId: session.agencyClientId }));
              }}
              onBack={() => setRemovalTarget(null)}
              onClose={() => setRemovalTarget(null)}
            />
          ) : null}
        </>
      ) : null}

      {resolved && setup && flowState.step === "delegatedView" ? (
        <DelegatedViewStep
          visible
          setup={setup}
          channels={channels}
          agencyClientId={session.agencyClientId}
          onClose={dismiss}
        />
      ) : null}

      {resolved && setup && catalog.data && flowState.step === "subBrands" ? (
        <SubBrandStep
          visible
          setup={setup}
          channels={channels}
          agencyClientId={session.agencyClientId}
          onComplete={finishSubBrands}
          {...(verificationInFlight || !accountsIpId ? {} : { onBack: () => goToStep("accounts", accountsIpId) })}
          onClose={dismiss}
        />
      ) : null}

      {quickTarget ? (
        <>
          <QuickConnectModal
            visible={!quickRemoval}
            target={quickTarget}
            logo={quickTarget.logo ?? null}
            agencyClientId={session.agencyClientId}
            onConnect={startQuickRedirect}
            onRemoveAccount={setQuickRemoval}
            onClose={closeQuick}
          />
          {quickRemoval ? (
            <RemoveAccountDialog
              visible
              channel={{ ipName: quickTarget.companyName ?? "", logo: quickTarget.logo ?? null } as ChannelCatalogItem}
              accountName={quickAccountName(quickRemoval)}
              onDisconnect={async () => {
                await IpRestS.uninstallUser(quickTarget._id ?? "", quickTarget.companyName ?? "", quickRemoval.id);
              }}
              onRemoved={() => {
                setQuickRemoval(null);
                void dispatch(fetchAccessTokens(session.agencyClientId ? `${session.agencyClientId}` : null) as never);
                void dispatch(fetchConnectionChannels({ agencyClientId: session.agencyClientId }));
              }}
              onBack={() => setQuickRemoval(null)}
              onClose={() => setQuickRemoval(null)}
            />
          ) : null}
        </>
      ) : null}
    </ConnectionHubContext.Provider>
  );
};
